export interface UtcScheduleTime {
  hour: number
  minute: number
}

export interface Local12HourTime {
  hour: number
  minute: number
  ampm: 'AM' | 'PM'
}

export function buildUtcDateOnReference(referenceDate: Date, time: UtcScheduleTime): Date {
  return new Date(Date.UTC(
    referenceDate.getUTCFullYear(),
    referenceDate.getUTCMonth(),
    referenceDate.getUTCDate(),
    time.hour,
    time.minute,
    0,
    0
  ))
}

export function getTimeZoneAbbreviation(referenceDate: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(referenceDate)
  return parts.find((part) => part.type === 'timeZoneName')?.value || 'Local'
}

export function utcTimeToLocal12Hour(time: UtcScheduleTime, referenceDate: Date = new Date()): Local12HourTime {
  const date = buildUtcDateOnReference(referenceDate, time)
  let localHour = date.getHours()
  const ampm: 'AM' | 'PM' = localHour >= 12 ? 'PM' : 'AM'
  localHour = localHour % 12
  localHour = localHour || 12
  return {
    hour: localHour,
    minute: date.getMinutes(),
    ampm
  }
}

export function utcTimesToLocal12Hour(times: UtcScheduleTime[], referenceDate: Date = new Date()): Local12HourTime[] {
  return times.map((time) => utcTimeToLocal12Hour(time, referenceDate))
}

export function local12HourToUtcTime(time: Local12HourTime, referenceDate: Date = new Date()): UtcScheduleTime {
  const date = new Date(referenceDate)
  let hour24 = time.hour
  if (time.ampm === 'PM' && hour24 < 12) {
    hour24 += 12
  }
  if (time.ampm === 'AM' && hour24 === 12) {
    hour24 = 0
  }
  date.setHours(hour24, time.minute, 0, 0)
  return {
    hour: date.getUTCHours(),
    minute: date.getUTCMinutes()
  }
}

export function local12HourTimesToUtc(times: Local12HourTime[], referenceDate: Date = new Date()): UtcScheduleTime[] {
  return times.map((time) => local12HourToUtcTime(time, referenceDate))
}

export function formatLocalDate(timestamp: number | Date): string {
  return new Date(timestamp).toLocaleDateString()
}

export function formatLocalTime(timestamp: number | Date): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  }).format(new Date(timestamp))
}

export function formatLocalDateTime(timestamp: number | Date): string {
  return `${formatLocalDate(timestamp)} ${formatLocalTime(timestamp)}`
}
